import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Download, CheckCircle2, ShieldCheck, FileDown, Loader2 } from "lucide-react";
import { api } from "@/lib/site";
import { getUTM } from "@/lib/utm";
import { toast } from "sonner";

export default function ResourceModal({
  open,
  onOpenChange,
  resourceKey = "entra-id-hardening-checklist",
  resourceTitle = "Microsoft Entra ID Hardening Checklist",
}) {
  const subtitles = {
    "entra-id-hardening-checklist":
      "A 25-point production-ready blueprint I take into Fortune-500 IAM hardening engagements. Delivered to your inbox instantly.",
    "az-500-exam-cheatsheet":
      "The exact 30-point study sheet I give to learners the day before they sit the Azure Security Engineer (AZ-500) exam. Delivered to your inbox instantly.",
    "cissp-domain-mastery":
      "The condensed view of all 8 CISSP CBK domains — the same framework I use in week one of my CISSP bootcamp. Delivered to your inbox instantly.",
  };
  const subtitle = subtitles[resourceKey] || subtitles["entra-id-hardening-checklist"];
  const submitLabel =
    resourceKey === "az-500-exam-cheatsheet" ? "Email me the cheatsheet"
    : resourceKey === "cissp-domain-mastery" ? "Email me the cheatsheet"
    : "Email me the checklist";
  const successNoun =
    resourceKey === "az-500-exam-cheatsheet" ? "cheatsheet"
    : resourceKey === "cissp-domain-mastery" ? "cheatsheet"
    : "checklist";
  const [form, setForm] = useState({ name: "", email: "", role: "", company: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setForm({ name: "", email: "", role: "", company: "" });
    setSuccess(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setLoading(true);
    try {
      await api.post("/resource-leads", { ...form, resource: resourceKey, utm: getUTM() });
      setSuccess(true);
      toast.success("Checklist sent. Check your inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 250);
      }}
    >
      <DialogContent
        data-testid="resource-modal"
        className="sm:max-w-md bg-[#0A0C13] border-white/10 text-white p-0 overflow-hidden"
      >
        {/* Header band */}
        <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-[#0E1424] to-[#0A0C13] border-b border-white/10">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-blue-300">
            Free resource · v2026.1
          </div>
          <DialogHeader className="mt-2 text-left">
            <DialogTitle className="font-display font-bold text-2xl tracking-tight text-white">
              {resourceTitle}
            </DialogTitle>
            <DialogDescription className="text-slate-400 mt-1 text-sm">
              {subtitle}
            </DialogDescription>
          </DialogHeader>
        </div>

        {success ? (
          <div className="px-6 py-10 text-center" data-testid="resource-success">
            <div className="mx-auto h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 grid place-items-center">
              <CheckCircle2 size={22} className="text-emerald-300" />
            </div>
            <div className="mt-5 font-display font-semibold text-white text-lg">
              On its way.
            </div>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              The full {successNoun} has just been emailed to <span className="text-white">{form.email}</span>.
              Check your inbox (and spam, just in case).
            </p>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="resource-success-close"
              className="mt-6 inline-flex items-center justify-center bg-white text-black font-medium px-6 py-2.5 rounded-full hover:bg-slate-200 transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-3" data-testid="resource-form">
            <Field label="Full name" required>
              <input
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="resource-input-name"
                className="rinput"
                placeholder="Jane Doe"
              />
            </Field>
            <Field label="Work email" required>
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="resource-input-email"
                className="rinput"
                placeholder="jane@company.com"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role">
                <input
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  data-testid="resource-input-role"
                  className="rinput"
                  placeholder="CISO, IAM Lead…"
                />
              </Field>
              <Field label="Company">
                <input
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  data-testid="resource-input-company"
                  className="rinput"
                  placeholder="Company"
                />
              </Field>
            </div>

            <div className="mt-2 flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed">
              <ShieldCheck size={12} className="mt-0.5 text-emerald-400 shrink-0" />
              <span>
                Your details stay private. I'll send you this checklist and the
                occasional insight — unsubscribe anytime.
              </span>
            </div>

            <DialogFooter className="!mt-5 !flex !flex-col sm:!flex-row sm:!justify-end gap-2">
              <button
                type="submit"
                disabled={loading || !form.name || !form.email}
                data-testid="resource-submit"
                className="inline-flex items-center justify-center gap-2 bg-white text-black font-medium px-6 py-3 rounded-full hover:bg-slate-200 disabled:opacity-60 transition-colors w-full sm:w-auto"
              >
                {loading ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
                {loading ? "Sending…" : "Email me the checklist"}
              </button>
            </DialogFooter>

            <style>{`
              .rinput {
                width: 100%;
                background: rgba(255,255,255,0.04);
                border: 1px solid rgba(255,255,255,0.10);
                color: #fff;
                border-radius: 0.6rem;
                padding: 0.7rem 0.9rem;
                font-size: 0.875rem;
                font-family: 'Satoshi', sans-serif;
                outline: none;
                transition: border 0.2s, background 0.2s;
              }
              .rinput:focus {
                border-color: rgba(59,130,246,0.6);
                background: rgba(255,255,255,0.06);
              }
              .rinput::placeholder { color: #64748B; }
            `}</style>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
        {label} {required && <span className="text-blue-400">*</span>}
      </span>
      {children}
    </label>
  );
}

export { Download };
